import { operationsService } from '@/services/operationsService';
import {
  Activity,
  ArrowLeft,
  Clock,
  Edit,
  FileText,
  MessageCircle,
  RefreshCw,
  Users
} from 'lucide-react';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { toast } from 'sonner';

import {
  ChainlitContext,
  threadOperationsEvents,
  useChatInteract,
  useChatSession
} from '@chainlit/react-client';
import {
  IOperation,
  isGeneratingOperationReportState,
  operationModalState,
  operationsDialogOpenState,
  operationsDialogViewState
} from '@chainlit/react-client';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';

interface OperationThread {
  id: string;
  name: string;
  preview: string;
  createdAt: string;
  messageCount: number;
  userId?: string;
  userIdentifier?: string;
  metadata?: Record<string, any>;
}

interface OperationDetailViewProps {
  operationId: string;
  skipInitialLoad?: boolean;
}

/**
 * Displays detailed view of an operation including threads, metadata, and actions.
 * Provides functionality to generate reports, export data, and navigate to threads.
 */
export default function OperationDetailView({
  operationId,
  skipInitialLoad = false
}: OperationDetailViewProps) {
  const apiClient = useContext(ChainlitContext);
  const navigate = useNavigate();
  const setModalState = useSetRecoilState(operationModalState);
  const setDialogView = useSetRecoilState(operationsDialogViewState);
  const setDialogOpen = useSetRecoilState(operationsDialogOpenState);
  const { sendOperationReportRequest } = useChatInteract();
  const { session } = useChatSession();

  const [operation, setOperation] = useState<IOperation | null>(null);
  const [threads, setThreads] = useState<OperationThread[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingThreads, setIsLoadingThreads] = useState(true);
  const [isGeneratingReport, setIsGeneratingReport] = useRecoilState(
    isGeneratingOperationReportState
  );
  const [showSkeleton, setShowSkeleton] = useState(false);

  useEffect(() => {
    if (operationId) {
      loadOperation();
      loadThreads();
    }
  }, [operationId]);

  useEffect(() => {
    if (skipInitialLoad) {
      return;
    }
    const timer = setTimeout(() => {
      if (isLoading) {
        setShowSkeleton(true);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [isLoading, skipInitialLoad]);

  useEffect(() => {
    const unsubscribe = threadOperationsEvents.subscribe(
      'operations_updated',
      () => {
        if (operationId) {
          loadOperation();
        }
      }
    );

    return unsubscribe;
  }, [operationId]);

  /**
   * Loads operation details from the API.
   */
  const loadOperation = async () => {
    if (!operationId) return;
    setIsLoading(true);
    try {
      const operationData = await operationsService.getOperationById(
        apiClient,
        operationId
      );
      setOperation(operationData);
    } catch (error) {
      console.error('Failed to load operation:', error);
      toast.error('Failed to load operation');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Loads all threads associated with this operation.
   */
  const loadThreads = async () => {
    setIsLoadingThreads(true);
    try {
      const threadsData = await operationsService.getThreadsForOperation(
        apiClient,
        operationId
      );
      setThreads(threadsData);
    } catch (error) {
      console.error('Failed to load threads:', error);
      toast.error('Failed to load threads');
    } finally {
      setIsLoadingThreads(false);
    }
  };

  /**
   * Navigates back to the operations list view.
   */
  const handleBack = () => {
    setDialogView({ view: 'list', operationId: null });
  };

  /**
   * Opens the edit modal for the current operation.
   */
  const handleEdit = () => {
    if (!operation) return;
    setModalState({
      isOpen: true,
      mode: 'edit',
      operationId: operation.id
    });
  };

  /**
   * Navigates to the selected thread and closes the dialog.
   */
  const handleThreadClick = (threadId: string) => {
    setDialogOpen(false);
    navigate(`/thread/${threadId}`);
  };

  /**
   * Triggers the shared report form to configure and generate the operation
   * report. The panel sends only the operation identifiers; the form (standard,
   * output format, advanced, notes, context files, custom template) is collected
   * by the backend, and the rendered report auto-downloads via the
   * ``download_file`` event (the DownloadListener).
   */
  const handleGenerateReport = () => {
    if (!operation) return;

    if (isGeneratingReport) {
      toast.warning('Report generation in progress', {
        description:
          'Please wait for the current report to finish before generating another.'
      });
      return;
    }

    setIsGeneratingReport(true);

    const reportData = {
      operationName: operation.name,
      operationUUID: operationId,
      threadUUIDs: threads.map((t) => t.id)
    };

    sendOperationReportRequest(
      reportData,
      (result: string) => {
        setIsGeneratingReport(false);
        // Empty/"cancelled" = the operator cancelled the form: silent no-op.
        if (!result || result === 'cancelled') return;
        toast.success('Report generated successfully!', {
          description: 'Your report has been downloaded.'
        });
      },
      (error: string) => {
        setIsGeneratingReport(false);
        toast.error('Operation report failed', {
          description: error || 'An unknown error occurred'
        });
      }
    );
  };

  /**
   * Exports complete chat history for all threads in the operation as markdown.
   */
  const handleExportChatHistory = async () => {
    try {
      setIsLoading(true);
      const exportData = await operationsService.exportOperationData(
        apiClient,
        operationId
      );

      const blob = new Blob([exportData.markdown], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${operation?.name || 'operation'}_export_${new Date().toISOString().split('T')[0]}.md`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('Operation exported successfully!');
    } catch (error) {
      toast.error('Failed to export operation');
      console.error('Export error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Formats a date string into a human-readable format.
   */
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading && showSkeleton) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!operation) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <p className="text-muted-foreground">Operation loading...</p>
        <Button onClick={handleBack} className="mt-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Operations
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:h-full px-2 py-1">
      <div className="space-y-4 mb-8">
        <div className="flex items-center justify-between pr-10">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBack}
            className="gap-2 hover:bg-primary/10 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Back to Operations</span>
            <span className="sm:hidden">Back</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleEdit}
            className="hover:bg-primary/10 hover:border-primary/50 transition-colors"
          >
            <Edit className="mr-2 h-4 w-4" />
            <span className="hidden sm:inline">Edit Operation</span>
            <span className="sm:hidden">Edit</span>
          </Button>
        </div>

        {/* Operation Title and Status */}
        <div className="flex items-center gap-4">
          <div
            className="w-8 h-8 rounded-lg shadow-md ring-2 ring-background"
            style={{ backgroundColor: operation.color }}
          />
          <div className="flex-1">
            <h1 className="text-2xl font-bold tracking-tight">
              {operation.name}
            </h1>
          </div>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="flex flex-col lg:flex-row lg:flex-1 lg:min-h-0 gap-6">
        <div className="flex-1 flex flex-col min-w-0">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h3 className="text-xl font-bold">Chat Threads</h3>
              <Badge variant="outline" className="text-xs">
                {threads.length} total
              </Badge>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={loadThreads}
              disabled={isLoadingThreads}
              className="hover:bg-primary/10 transition-colors"
            >
              <RefreshCw
                className={`h-4 w-4 ${isLoadingThreads ? 'animate-spin' : ''}`}
              />
            </Button>
          </div>
          <div className="min-h-[300px] lg:min-h-0 lg:flex-1 rounded-xl p-4 sm:p-6 border-2 shadow-inner bg-muted/40">
            <div className="h-full overflow-y-auto space-y-4 pr-2 scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent">
              {isLoadingThreads ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-24 w-full" />
                ))
              ) : threads.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full py-16">
                  <div className="w-16 h-16 bg-muted/30 rounded-full flex items-center justify-center mb-4">
                    <MessageCircle className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h4 className="text-lg font-semibold text-muted-foreground mb-2">
                    No threads yet
                  </h4>
                  <p className="text-muted-foreground text-center max-w-sm">
                    Add threads to this operation by clicking the folder icon in
                    your chat conversations
                  </p>
                </div>
              ) : (
                threads.map((thread) => (
                  <Card
                    key={thread.id}
                    className="group hover:shadow-lg hover:shadow-primary/10 transition-all duration-200 cursor-pointer bg-card hover:bg-accent/30 border-2 hover:border-primary/30"
                    onClick={() => handleThreadClick(thread.id)}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <CardTitle className="text-base line-clamp-1 group-hover:text-primary transition-colors">
                            {thread.name || 'Untitled Thread'}
                          </CardTitle>
                          <div className="flex items-center gap-2 mt-1">
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Clock className="h-3 w-3" />
                              <span>{formatDate(thread.createdAt)}</span>
                            </div>
                            {thread.userIdentifier && (
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Users className="h-3 w-3" />
                                <span>{thread.userIdentifier}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <Badge
                          variant="secondary"
                          className="ml-2 text-xs px-2 py-1"
                        >
                          {thread.messageCount} msgs
                        </Badge>
                      </div>
                    </CardHeader>
                    {thread.preview && (
                      <CardContent className="pt-0">
                        <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                          {thread.preview}
                        </p>
                      </CardContent>
                    )}
                  </Card>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Operation Details */}
        <div className="w-full lg:w-[420px] flex flex-col space-y-6">
          <Card className="border-2 shadow-lg">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div
                  className="w-12 h-12 rounded-lg flex-shrink-0 shadow-md ring-2 ring-background"
                  style={{ backgroundColor: operation.color }}
                />
                <div className="flex-1 min-w-0">
                  <h2 className="text-xl font-bold line-clamp-1">
                    {operation.name}
                  </h2>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <span className="text-sm font-semibold text-muted-foreground">
                  Description
                </span>
                <p className="text-sm leading-relaxed bg-muted/30 p-3 rounded-lg">
                  {operation.description || 'No description provided'}
                </p>
              </div>

              <Separator />

              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-foreground">
                  Details
                </h3>
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span className="font-medium">Created</span>
                  </div>
                  <p className="text-sm font-semibold text-right">
                    {formatDate(operation.createdAt)}
                  </p>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    Total Messages
                  </span>
                  <span className="text-sm font-semibold">
                    {threads.reduce(
                      (sum, thread) => sum + thread.messageCount,
                      0
                    )}
                  </span>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      Threads
                    </span>
                    <span className="text-sm font-semibold">
                      {threads.length}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions Card */}
          <Card className="border-2 shadow-lg">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg">Actions</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start h-10 hover:bg-primary/10 hover:border-primary/50 transition-colors"
                onClick={() => handleGenerateReport()}
                disabled={isGeneratingReport || !threads.length}
              >
                <FileText className="mr-2 h-4 w-4" />
                {isGeneratingReport ? 'Generating...' : 'Generate Report'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start h-10 hover:bg-primary/10 hover:border-primary/50 transition-colors"
                onClick={handleExportChatHistory}
                disabled={isLoading}
              >
                <MessageCircle className="mr-2 h-4 w-4" />
                {isLoading ? 'Exporting...' : 'Export Chat History'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
